import type { ComponentProps, FunctionComponent } from 'react';
import type React from 'react';

export type IconProps = ComponentProps<'svg'> & {
  size?: number | string;
};

const createIcon = (path: React.ReactNode, displayName: string): FunctionComponent<IconProps> => {
  const Icon: FunctionComponent<IconProps> = ({ size = 24, className, ...props }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <title>{displayName}</title>
      {path}
    </svg>
  );
  Icon.displayName = displayName;
  return Icon;
};

export const Trash2 = createIcon(
  <>
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" x2="10" y1="11" y2="17" />
    <line x1="14" x2="14" y1="11" y2="17" />
  </>,
  'Trash2',
);

export const Download = createIcon(
  <>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" x2="12" y1="15" y2="3" />
  </>,
  'Download',
);

export const Heading = createIcon(
  <>
    <path d="M6 12h12" />
    <path d="M6 18V6" />
    <path d="M18 18V6" />
  </>,
  'Heading',
);

export const Heading1 = createIcon(
  <>
    <path d="M4 12h8" />
    <path d="M4 18V6" />
    <path d="M12 18V6" />
    <path d="m17 12 3-2v8" />
  </>,
  'Heading1',
);

export const ArrowLeft = createIcon(
  <>
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </>,
  'ArrowLeft',
);

export const Columns3 = createIcon(
  <>
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M9 3v18" />
    <path d="M15 3v18" />
  </>,
  'Columns3',
);

export const LayoutGrid = createIcon(
  <>
    <rect width="7" height="7" x="3" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="14" rx="1" />
    <rect width="7" height="7" x="3" y="14" rx="1" />
  </>,
  'LayoutGrid',
);

export const Minus = createIcon(<path d="M5 12h14" />, 'Minus');

export const Plus = createIcon(
  <>
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </>,
  'Plus',
);

export const Rows3 = createIcon(
  <>
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M21 9H3" />
    <path d="M21 15H3" />
  </>,
  'Rows3',
);

export const Edit3 = createIcon(
  <>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </>,
  'Edit3',
);

export const ExternalLink = createIcon(
  <>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" x2="21" y1="14" y2="3" />
  </>,
  'ExternalLink',
);

export const X = createIcon(
  <>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </>,
  'X',
);

export const Bold = createIcon(
  <>
    <path d="M14 12a4 4 0 0 0 0-8H6v8" />
    <path d="M15 20a4 4 0 0 0 0-8H6v8Z" />
  </>,
  'Bold',
);

export const Italic = createIcon(
  <>
    <line x1="19" x2="10" y1="4" y2="4" />
    <line x1="14" x2="5" y1="20" y2="20" />
    <line x1="15" x2="9" y1="4" y2="20" />
  </>,
  'Italic',
);

export const Strikethrough = createIcon(
  <>
    <path d="M16 4H9a3 3 0 0 0-2.83 4" />
    <path d="M14 12a4 4 0 0 1 0 8H6" />
    <line x1="4" x2="20" y1="12" y2="12" />
  </>,
  'Strikethrough',
);

export const Code = createIcon(
  <>
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </>,
  'Code',
);

export const Quote = createIcon(
  <>
    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
  </>,
  'Quote',
);

export const FileCode = createIcon(
  <>
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="m9 13-2 2 2 2" />
    <path d="m15 13 2 2-2 2" />
  </>,
  'FileCode',
);

export const List = createIcon(
  <>
    <line x1="8" x2="21" y1="6" y2="6" />
    <line x1="8" x2="21" y1="12" y2="12" />
    <line x1="8" x2="21" y1="18" y2="18" />
    <line x1="3" x2="3.01" y1="6" y2="6" />
    <line x1="3" x2="3.01" y1="12" y2="12" />
    <line x1="3" x2="3.01" y1="18" y2="18" />
  </>,
  'List',
);

export const ListOrdered = createIcon(
  <>
    <line x1="10" x2="21" y1="6" y2="6" />
    <line x1="10" x2="21" y1="12" y2="12" />
    <line x1="10" x2="21" y1="18" y2="18" />
    <path d="M4 6h1v4" />
    <path d="M4 10h2" />
    <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
  </>,
  'ListOrdered',
);

export const Link2 = createIcon(
  <>
    <path d="M9 17H7A5 5 0 0 1 7 7h2" />
    <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
    <line x1="8" x2="16" y1="12" y2="12" />
  </>,
  'Link2',
);

export const Image = createIcon(
  <>
    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </>,
  'Image',
);

export const Table = createIcon(
  <>
    <path d="M12 3v18" />
    <rect width="18" height="18" x="3" y="3" rx="2" />
    <path d="M3 9h18" />
    <path d="M3 15h18" />
  </>,
  'Table',
);

export const Smile = createIcon(
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" x2="9.01" y1="9" y2="9" />
    <line x1="15" x2="15.01" y1="9" y2="9" />
  </>,
  'Smile',
);

export type LucideIcon = FunctionComponent<IconProps>;

export const AlignLeft = createIcon(
  <>
    <line x1="17" y1="10" x2="3" y2="10" />
    <line x1="21" y1="6" x2="3" y2="6" />
    <line x1="21" y1="14" x2="3" y2="14" />
    <line x1="17" y1="18" x2="3" y2="18" />
  </>,
  'AlignLeft',
);

export const AlignCenter = createIcon(
  <>
    <line x1="21" y1="6" x2="3" y2="6" />
    <line x1="17" y1="12" x2="7" y2="12" />
    <line x1="19" y1="18" x2="5" y2="18" />
  </>,
  'AlignCenter',
);

export const AlignRight = createIcon(
  <>
    <line x1="21" y1="10" x2="7" y2="10" />
    <line x1="21" y1="6" x2="3" y2="6" />
    <line x1="21" y1="14" x2="3" y2="14" />
    <line x1="21" y1="18" x2="7" y2="18" />
  </>,
  'AlignRight',
);

/* Icons for text wrapping (Float) */
export const WrapText = createIcon(
  <>
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M3 12h15a3 3 0 1 1 0 6h-4" />
    <polyline points="16 16 14 18 16 20" />
  </>,
  'WrapText',
);

/* Using specific icons for Float Left/Right visualization */
export const AlignHorizontalDistributeStart = createIcon(
  <>
    <rect width="6" height="14" x="4" y="5" rx="2" />
    <path d="M14 10h7" />
    <path d="M14 14h7" />
    <path d="M14 6h7" />
  </>,
  'AlignHorizontalDistributeStart', // Float Left
);

export const AlignHorizontalDistributeEnd = createIcon(
  <>
    <rect width="6" height="14" x="14" y="5" rx="2" />
    <path d="M10 10H3" />
    <path d="M10 14H3" />
    <path d="M10 6H3" />
  </>,
  'AlignHorizontalDistributeEnd', // Float Right
);

/* Size icons (Simple squares) */
export const SizeSmall = createIcon(
  <rect x="10" y="10" width="4" height="4" rx="0.5" />,
  'SizeSmall',
);

export const SizeMedium = createIcon(
  <rect x="7" y="7" width="10" height="10" rx="1" />,
  'SizeMedium',
);

export const SizeLarge = createIcon(
  <rect x="3" y="3" width="18" height="18" rx="2" />,
  'SizeLarge',
);
